import React, { useState, useEffect } from 'react';
import * as cloudflareService from '../services/cloudflareService';
import {
    Users, Shield, LogOut as LogOutIcon, Globe, Laptop, Smartphone, RefreshCw, Filter
} from 'lucide-react';

interface StaffSessionsPanelProps {
    /** If set, only fetch sessions for this hospital */
    hospitalId?: string;
    /** Map of hospitalId -> hospitalName for resolving names (global view) */
    hospitals?: { id: string; name: string }[];
    /** Primary accent color */
    primaryColor?: string;
    /** Theme tokens for dark/light mode in Super Admin */
    theme?: 'dark' | 'light';
    /** Callback on unauthorized to handle redirect */
    /** Callback on unauthorized to handle redirect */
    onUnauthorized?: () => void;
}

const parseUTCDate = (dateStr: string) => {
    if (!dateStr) return new Date();
    // If already has Z or timezone offset, parse natively
    if (dateStr.includes('Z') || dateStr.includes('T') && (dateStr.includes('+') || dateStr.match(/-\d{2}:\d{2}$/))) {
        return new Date(dateStr);
    }
    // SQLite format 'YYYY-MM-DD HH:MM:SS' needs 'T' and 'Z' to be parsed strictly as UTC
    return new Date(dateStr.replace(' ', 'T') + 'Z');
};

const ROLE_COLORS: Record<string, { dark: string; light: string }> = {
    SUPER_ADMIN:    { dark: 'bg-red-500/10 text-red-400 border-red-500/20', light: 'bg-red-50 text-red-600 border-red-100' },
    GROUP_ADMIN:    { dark: 'bg-purple-500/10 text-purple-400 border-purple-500/20', light: 'bg-purple-50 text-purple-600 border-purple-100' },
    HOSPITAL_ADMIN: { dark: 'bg-violet-500/10 text-violet-400 border-violet-500/20', light: 'bg-violet-50 text-violet-600 border-violet-100' },
    FINANCIERO:     { dark: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20', light: 'bg-emerald-50 text-emerald-600 border-emerald-100' },
    OPERATIVO:      { dark: 'bg-blue-500/10 text-blue-400 border-blue-500/20', light: 'bg-blue-50 text-blue-600 border-blue-100' },
    STAFF:          { dark: 'bg-slate-500/10 text-slate-400 border-slate-500/20', light: 'bg-slate-50 text-slate-500 border-slate-200' },
};

const ROLE_LABELS: Record<string, string> = {
    SUPER_ADMIN: 'Super Admin',
    GROUP_ADMIN: 'Director Cadena',
    HOSPITAL_ADMIN: 'Admin Hospital',
    FINANCIERO: 'Financiero',
    OPERATIVO: 'Operativo',
    STAFF: 'Personal',
};

const StaffSessionsPanel: React.FC<StaffSessionsPanelProps> = ({
    hospitalId,
    hospitals = [],
    primaryColor = '#6366F1',
    theme = 'light',
    onUnauthorized,
}) => {
    const [sessions, setSessions] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [revokingId, setRevokingId] = useState<string | null>(null);
    const [filterHospitalId, setFilterHospitalId] = useState<string | null>(null);
    const [filterRole, setFilterRole] = useState<string | null>(null);

    const isDark = theme === 'dark';

    const loadSessions = async () => {
        setLoading(true);
        try {
            const data = await cloudflareService.getStaffSessions(hospitalId);
            // Si no se especifica un hospital específico, pero tenemos la lista de hospitales
            // de la red/grupo actual, filtramos para no mostrar sesiones de hospitales fantasma
            const filteredData = (!hospitalId && hospitals && hospitals.length > 0)
                ? data.filter((s: any) => hospitals.some(h => h.id === s.hospitalId))
                : data;
            setSessions(filteredData);
        } catch (err: any) {
            if (err.message === 'UNAUTHORIZED' && onUnauthorized) {
                onUnauthorized();
                return;
            }
            setSessions([]);
        }
        setLoading(false);
    };

    useEffect(() => {
        loadSessions();
    }, [hospitalId]);

    const handleRevoke = async (sessionId: string) => {
        if (!window.confirm('¿Revocar esta sesión? El usuario tendrá que volver a iniciar sesión.')) return;
        setRevokingId(sessionId);
        try {
            await cloudflareService.revokeStaffSession(sessionId);
            setSessions(prev => prev.filter(s => s.id !== sessionId));
        } catch (err) {
            console.error('Failed to revoke session', err);
        }
        setRevokingId(null);
    };

    // Apply filters
    const filtered = sessions.filter(s => {
        if (filterHospitalId && s.hospitalId !== filterHospitalId) return false;
        if (filterRole && (s.staffRole || 'STAFF') !== filterRole) return false;
        return true;
    });

    // Unique hospitals and roles from sessions for filter dropdowns
    const sessionHospitalIds = [...new Set(sessions.map(s => s.hospitalId))];
    const sessionRoles = [...new Set(sessions.map(s => s.staffRole || 'STAFF'))];

    const getHospitalName = (hId: string) => {
        const h = hospitals.find(h => h.id === hId);
        return h?.name || hId.substring(0, 12) + '...';
    };

    const getDeviceIcon = (os: string) => {
        if (os === 'iOS' || os === 'Android') return <Smartphone className="w-3.5 h-3.5" />;
        if (os === 'macOS' || os === 'Windows' || os === 'Linux') return <Laptop className="w-3.5 h-3.5" />;
        return <Globe className="w-3.5 h-3.5" />;
    };

    // Skeleton
    if (loading && sessions.length === 0) {
        return (
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDark ? 'bg-indigo-500/10' : ''}`} style={{ backgroundColor: isDark ? undefined : `${primaryColor}12` }}>
                            <Users className="w-5 h-5" style={{ color: primaryColor }} />
                        </div>
                        <div>
                            <h2 className={`text-lg font-black tracking-tight ${isDark ? 'text-white' : 'text-slate-800'}`}>Sesiones Activas</h2>
                            <p className={`text-xs font-medium ${isDark ? 'text-slate-400' : 'text-slate-400'}`}>Cargando...</p>
                        </div>
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[1, 2, 3, 4].map(i => (
                        <div key={i} className={`rounded-2xl border p-5 animate-pulse ${isDark ? 'bg-white/[0.02] border-white/5' : 'bg-white border-slate-100'}`}>
                            <div className="flex items-center gap-3 mb-4">
                                <div className={`w-10 h-10 rounded-full ${isDark ? 'bg-white/10' : 'bg-slate-100'}`} />
                                <div className="flex-1">
                                    <div className={`h-3 rounded w-28 mb-2 ${isDark ? 'bg-white/10' : 'bg-slate-100'}`} />
                                    <div className={`h-2 rounded w-16 ${isDark ? 'bg-white/5' : 'bg-slate-50'}`} />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <div className={`h-2 rounded w-full ${isDark ? 'bg-white/5' : 'bg-slate-50'}`} />
                                <div className={`h-2 rounded w-3/4 ${isDark ? 'bg-white/5' : 'bg-slate-50'}`} />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDark ? 'bg-indigo-500/10' : ''}`} style={{ backgroundColor: isDark ? undefined : `${primaryColor}12` }}>
                        <Users className="w-5 h-5" style={{ color: primaryColor }} />
                    </div>
                    <div>
                        <h2 className={`text-lg font-black tracking-tight ${isDark ? 'text-white' : 'text-slate-800'}`}>Sesiones Activas</h2>
                        <p className={`text-xs font-medium ${isDark ? 'text-slate-400' : 'text-slate-400'}`}>
                            {filtered.filter(s => s.isActive).length} conectado{filtered.filter(s => s.isActive).length !== 1 ? 's' : ''} de {filtered.length} sesion{filtered.length !== 1 ? 'es' : ''} en últimos 30 días
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {/* Hospital filter (only show if global view without fixed hospitalId) */}
                    {!hospitalId && sessionHospitalIds.length > 0 && (
                        <select
                            value={filterHospitalId || ''}
                            onChange={e => setFilterHospitalId(e.target.value || null)}
                            className={`text-xs font-bold px-3 py-2 rounded-xl border appearance-none cursor-pointer transition-all ${isDark
                                ? 'bg-white/5 border-white/10 text-slate-300 hover:bg-white/10'
                                : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                            }`}
                        >
                            <option value="">Todos los hospitales</option>
                            {sessionHospitalIds.map(hId => (
                                <option key={hId} value={hId}>{getHospitalName(hId)}</option>
                            ))}
                        </select>
                    )}
                    {/* Role filter */}
                    {sessionRoles.length > 1 && (
                        <select
                            value={filterRole || ''}
                            onChange={e => setFilterRole(e.target.value || null)}
                            className={`text-xs font-bold px-3 py-2 rounded-xl border appearance-none cursor-pointer transition-all ${isDark
                                ? 'bg-white/5 border-white/10 text-slate-300 hover:bg-white/10'
                                : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                            }`}
                        >
                            <option value="">Todos los roles</option>
                            {sessionRoles.map(r => (
                                <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>
                            ))}
                        </select>
                    )}
                    <button
                        onClick={loadSessions}
                        className={`flex items-center gap-2 text-xs font-bold px-4 py-2.5 rounded-xl border transition-all ${isDark
                            ? 'border-white/10 text-slate-300 hover:bg-white/5'
                            : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                        }`}
                    >
                        <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                        Actualizar
                    </button>
                </div>
            </div>

            {/* Empty State */}
            {filtered.length === 0 ? (
                <div className={`rounded-3xl border shadow-sm p-12 text-center ${isDark ? 'bg-white/[0.02] border-white/5' : 'bg-white border-slate-100'}`}>
                    <Users className={`w-10 h-10 mx-auto mb-3 ${isDark ? 'text-slate-600' : 'text-slate-300'}`} />
                    <p className={`font-bold mb-1 ${isDark ? 'text-slate-300' : 'text-slate-500'}`}>Sin sesiones activas</p>
                    <p className={`text-sm ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Las sesiones aparecerán aquí cuando el personal inicie sesión.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {filtered.map(session => {
                        const lastActivity = parseUTCDate(session.lastActivityAt);
                        const loginTime = parseUTCDate(session.loginAt);
                        
                        const minutesAgo = Math.round((Date.now() - lastActivity.getTime()) / 60000);
                        const timeLabel = minutesAgo < 1 ? 'Ahora' : minutesAgo < 60 ? `hace ${minutesAgo}m` : minutesAgo < 1440 ? `hace ${Math.round(minutesAgo / 60)}h` : `hace ${Math.round(minutesAgo / 1440)}d`;
                        
                        const durationMins = Math.round((Date.now() - loginTime.getTime()) / 60000);
                        const durationLabel = durationMins < 60 ? `${durationMins}m` : `${Math.floor(durationMins / 60)}h ${durationMins % 60}m`;

                        const roleKey = session.staffRole || 'STAFF';
                        const roleColor = ROLE_COLORS[roleKey]?.[theme] || ROLE_COLORS.STAFF[theme];
                        const roleLabel = ROLE_LABELS[roleKey] || roleKey;
                        const hospName = !hospitalId ? getHospitalName(session.hospitalId) : null;

                        return (
                            <div key={session.id} className={`rounded-2xl border shadow-sm hover:shadow-md transition-all group relative overflow-hidden ${isDark
                                ? `bg-white/[0.02] ${session.isActive ? 'border-emerald-500/20' : 'border-white/5'}`
                                : `bg-white ${session.isActive ? 'border-emerald-200' : 'border-slate-100'}`
                            }`}>
                                {session.isActive && <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-500/5 blur-[40px] rounded-full pointer-events-none" />}

                                <div className="p-5">
                                    {/* Top row: Avatar + Name + Revoke */}
                                    <div className="flex items-start justify-between mb-3">
                                        <div className="flex items-center gap-3">
                                            <div className="relative">
                                                <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-black text-white" style={{ backgroundColor: primaryColor }}>
                                                    {session.staffName?.charAt(0)?.toUpperCase() || '?'}
                                                </div>
                                                <span className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 ${isDark ? 'border-[#0B1120]' : 'border-white'} ${session.isActive ? 'bg-emerald-400' : 'bg-slate-300'}`}>
                                                    {session.isActive && <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-75" />}
                                                </span>
                                            </div>
                                            <div>
                                                <h4 className={`font-bold text-sm ${isDark ? 'text-white' : 'text-slate-800'}`}>{session.staffName}</h4>
                                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${roleColor}`}>
                                                    <Shield className="w-2.5 h-2.5" />
                                                    {roleLabel}
                                                </span>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => handleRevoke(session.id)}
                                            disabled={revokingId === session.id}
                                            className={`p-2 rounded-xl opacity-0 group-hover:opacity-100 transition-all disabled:opacity-50 ${isDark
                                                ? 'text-slate-500 hover:text-red-400 hover:bg-red-400/10'
                                                : 'text-slate-300 hover:text-red-500 hover:bg-red-50'
                                            }`}
                                            title="Revocar sesión"
                                        >
                                            {revokingId === session.id
                                                ? <div className="w-4 h-4 border-2 border-red-300 border-t-red-600 rounded-full animate-spin" />
                                                : <LogOutIcon className="w-4 h-4" />
                                            }
                                        </button>
                                    </div>

                                    {/* Hospital badge (global view only) */}
                                    {hospName && (
                                        <div className={`mb-3 px-2.5 py-1 rounded-lg text-[10px] font-bold inline-flex items-center gap-1 ${isDark
                                            ? 'bg-indigo-500/10 text-indigo-400 border border-indigo-500/20'
                                            : 'bg-indigo-50 text-indigo-600 border border-indigo-100'
                                        }`}>
                                            🏥 {hospName}
                                        </div>
                                    )}

                                    {/* Details grid */}
                                    <div className="space-y-2 text-xs">
                                        <div className={`flex justify-between ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                                            <span className="flex items-center gap-1.5">{getDeviceIcon(session.os || '')} Dispositivo</span>
                                            <span className={`font-bold ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>{session.browser || '—'} / {session.os || '—'}</span>
                                        </div>
                                        <div className={`flex justify-between ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                                            <span className="flex items-center gap-1.5"><Globe className="w-3.5 h-3.5" /> IP</span>
                                            <span className={`font-mono font-bold ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>{session.ipAddress || '—'}</span>
                                        </div>
                                        <div className={`flex justify-between ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                                            <span>Última actividad</span>
                                            <span className={`font-bold ${session.isActive ? 'text-emerald-500' : (isDark ? 'text-slate-500' : 'text-slate-400')}`}>{timeLabel}</span>
                                        </div>
                                        <div className={`flex justify-between ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                                            <span>Duración</span>
                                            <span className={`font-bold ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>{durationLabel}</span>
                                        </div>
                                        <div className={`flex justify-between ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                                            <span>Inicio de sesión</span>
                                            <span className={`font-mono ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>{loginTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default StaffSessionsPanel;
